import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useUser } from '../hooks/useUser';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { Bars3Icon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AppsIcon from '@mui/icons-material/Apps';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@assets/logoicon2-128.png';

const Navbar = () => {
  const { signOut, status, user } = useAuthContext();
  const { userDoc } = useUser(user);
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAppsConfirmOpen, setIsAppsConfirmOpen] = useState(false);
  const isAdmin = userDoc?.role === 'admin' || user?.email === 'jpigra@gmail.com';

  const appsUrl = 'https://appstore.footballcoachs.com';

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleAppsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsAppsConfirmOpen(true);
  };

  const confirmOpenApps = () => {
    window.open(appsUrl, '_blank', 'noopener,noreferrer');
    setIsAppsConfirmOpen(false);
  };

  // Styles pour améliorer le contraste
  const menuItemClass = "text-gray-700 hover:bg-gray-100 hover:text-gray-900 block px-4 py-3 rounded-md text-base font-medium transition-colors duration-200";
  const buttonClass = "w-full flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-white transition-all duration-200";
  
  return (
    <nav className="bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-gray-200 shadow-sm h-16 px-2 w-full fixed top-0 left-0 right-0 z-[9999]">
      <div className="max-w-7xl mx-auto px-1 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center mr-8">
                <img
                  className="h-10 w-auto"
                  src={logo}
                  alt="ProgramFoot"
                />
              </Link>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:ml-6 md:flex md:items-center md:space-x-4">
            <button
              type="button"
              onClick={handleAppsClick}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              title="Ouvrir le tableau de bord Football Coachs"
              aria-label="Ouvrir le tableau de bord Football Coachs"
            >
              <AppsIcon fontSize="small" />
            </button>
            <Link to="/" className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200">
              Accueil
            </Link>
            {status === 'authenticated' && (
              <>
                <Link to="/dashboard" className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200">
                  Tableau de bord
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200">
                    Administration
                  </Link>
                )}
                <Link 
                  to="/programmation/create" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md text-sm transition-all duration-200 shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white"
                >
                  Nouvelle planification
                </Link>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="hidden md:ml-4 md:flex-shrink-0 md:flex md:items-center">
            {status === 'authenticated' ? (
              <Menu as="div" className="relative">
                <div>
                  <Menu.Button className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white">
                    <span className="sr-only">Ouvrir le menu utilisateur</span>
                    <UserCircleIcon className="h-5 w-5 text-gray-600" />
                    <span className="max-w-[10rem] truncate">
                      {userDoc?.displayName || 'Compte'}
                    </span>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-xl bg-white ring-2 ring-blue-500/20 focus:outline-none z-50 overflow-hidden">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/dashboard"
                          className={`${
                            active ? 'bg-blue-50 text-blue-800' : 'text-gray-800'
                          } block px-4 py-3 text-sm font-medium hover:bg-blue-50 transition-colors duration-200`}
                        >
                          Tableau de bord
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`${
                            active ? 'bg-blue-50 text-blue-800' : 'text-gray-800'
                          } block px-4 py-3 text-sm font-medium hover:bg-blue-50 transition-colors duration-200`}
                        >
                          Mon profil
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <div className="flex justify-center w-full px-2 py-1">
                          <button
                            onClick={handleSignOut}
                            className={`${
                              active ? 'bg-orange-100' : 'bg-orange-500 hover:bg-orange-600'
                            } w-full max-w-xs flex justify-center items-center px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200`}
                          >
                            Déconnexion
                          </button>
                        </div>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <div className="flex flex-wrap justify-center gap-2 no-print">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-sm hover:shadow"
                >
                  S'inscrire
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center space-x-2 md:hidden">
            <button
              type="button"
              onClick={handleAppsClick}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              title="Ouvrir le tableau de bord Football Coachs"
              aria-label="Ouvrir le tableau de bord Football Coachs"
            >
              <AppsIcon fontSize="small" />
            </button>
            <div className="-mr-2 flex items-center md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500 transition-colors duration-200"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                aria-label="Ouvrir le menu principal"
              >
                <span className="sr-only">Ouvrir le menu principal</span>
                {isMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-white shadow-lg fixed inset-x-0 top-16 bottom-0 z-[10000] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-4 pb-8 space-y-2 sm:px-6">
              <Link
                to="/"
                className={menuItemClass}
                onClick={() => setIsMenuOpen(false)}
                aria-current="page"
              >
                Accueil
              </Link>
              {status === 'authenticated' && (
                <>
                  <Link
                    to="/dashboard"
                    className={menuItemClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Tableau de bord
                  </Link>
                  <Link
                    to="/profile"
                    className={menuItemClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mon profil
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className={menuItemClass}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Administration
                    </Link>
                  )}
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <Link
                      to="/programmation/create"
                      className={buttonClass}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Nouvelle planification
                    </Link>
                  </div>
                  <div className="flex justify-center w-full pt-4 border-t border-gray-200 mt-4">
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="w-full max-w-xs flex justify-center items-center px-4 py-3 text-base font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors duration-200"
                    >
                      Déconnexion
                    </button>
                  </div>
                </>
              )}
              {status !== 'authenticated' && (
                <div className="space-y-4 pt-4">
                  <Link
                    to="/login"
                    className={menuItemClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className={buttonClass}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[9990]"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <Dialog
        open={isAppsConfirmOpen}
        onClose={() => setIsAppsConfirmOpen(false)}
        aria-labelledby="apps-confirm-title"
        aria-describedby="apps-confirm-description"
      >
        <DialogTitle id="apps-confirm-title">Ouvrir Football Coachs</DialogTitle>
        <DialogContent>
          <DialogContentText id="apps-confirm-description">
            Voulez-vous ouvrir le tableau de bord Football Coachs dans un nouvel onglet ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setIsAppsConfirmOpen(false)}>
            Annuler
          </Button>
          <Button variant="contained" onClick={confirmOpenApps}>
            Ouvrir
          </Button>
        </DialogActions>
      </Dialog>
    </nav>
  );
};

export default Navbar;
